/*
 * KnowledgeHub — browse and edit the content taxonomy that every mode draws from
 * (Topic → Sub-topic → Concept → Node, including DSA). Two views:
 *   • Table  — the tree as expandable rows with a mastery overlay + inline edit.
 *   • Graph  — a radial, drill-down map coloured by mastery.
 * Reads GET /hub/tree + GET /engine/memory; edits via the /hub write endpoints.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import '../../styles/knowledge.css';
import APIService from '../../services/APIService';
import { IconUpload } from './icons';

const I = ({ d, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const Caret = ({ open }) => <I d={open ? <polyline points="6 9 12 15 18 9" /> : <polyline points="9 6 15 12 9 18" />} size={13} />;
const Plus = () => <I d={<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>} />;
const Pencil = () => <I d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>} />;
const Trash = () => <I d={<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>} />;

const masteryClass = (m) => (m >= 0.75 ? 'strong' : m >= 0.4 ? 'mid' : 'weak');
const masteryColor = (m) => (m == null ? 'var(--n-border-2)' : m >= 0.75 ? '#10b981' : m >= 0.4 ? '#f59e0b' : '#ef4444');
const parentOf = (id) => (id.includes('.') ? id.slice(0, id.lastIndexOf('.')) : 'ROOT');

export default function KnowledgeHub() {
  const [tree, setTree] = useState([]);
  const [ks, setKs] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [view, setView] = useState('table');
  const [expanded, setExpanded] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState('ROOT');

  const [addUnder, setAddUnder] = useState(null);
  const [addName, setAddName] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [items, setItems] = useState({});          // nodeId -> item[] (lazy-loaded)
  const [addingItem, setAddingItem] = useState(null);
  const [itemText, setItemText] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [hub, mem] = await Promise.all([
        APIService.getHubTree(),
        APIService.getEngineMemory().catch(() => ({ knowledge_state: {} })),
      ]);
      setTree(hub.tree || []);
      setKs(mem.knowledge_state || {});
    } catch (e) {
      setErr(e.message || 'Could not load the Knowledge Hub.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Upload a .md file → a new topic (headings → structure, bullets/?-lines →
  // depth-tagged questions). The file is read client-side and posted as text.
  const [importing, setImporting] = useState(false);
  const importMarkdownFile = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                      // allow re-selecting the same file
    if (!file) return;
    setImporting(true); setErr('');
    try {
      const markdown = await file.text();
      const name = file.name.replace(/\.(md|markdown)$/i, '').replace(/[-_]+/g, ' ').trim();
      const res = await APIService.importMarkdown({ markdown, name });
      await load();
      window.alert(`Imported "${name}": ${res.nodes} nodes, ${res.items} questions. Every mode can use it now.`);
    } catch (ex) {
      setErr(ex.message || 'Markdown import failed.');
    } finally {
      setImporting(false);
    }
  }, [load]);

  const fetchItems = useCallback(async (id) => {
    try { const d = await APIService.getHubNode(id); setItems((p) => ({ ...p, [id]: d.items || [] })); }
    catch { /* ignore */ }
  }, []);
  const toggleNode = (node) => {
    setExpanded((p) => { const n = new Set(p); n.has(node.id) ? n.delete(node.id) : n.add(node.id); return n; });
    if ((node.item_count > 0 || node.kind === 'node') && !items[node.id]) fetchItems(node.id);
  };

  const doAddItem = async (nodeId) => {
    const q = itemText.trim(); if (!q) return;
    setBusy(true);
    try {
      await APIService.createHubItem({ nodeId, type: 'interview_q', payload: { question: q }, modes: ['theory', 'flashcards', 'interviewer', 'mcq'] });
      setItemText(''); setAddingItem(null); await fetchItems(nodeId); await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const doDeleteItem = async (nodeId, itemId) => {
    setBusy(true);
    try { await APIService.deleteHubItem(itemId); await fetchItems(nodeId); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const { byId, childrenOf } = useMemo(() => {
    const b = {}, c = { ROOT: [] };
    const walk = (nodes, parent) => (nodes || []).forEach((n) => {
      b[n.id] = n; (c[parent] = c[parent] || []).push(n); walk(n.children, n.id);
    });
    walk(tree, 'ROOT');
    return { byId: b, childrenOf: c };
  }, [tree]);

  const counts = useMemo(() => {
    let topics = 0, nodes = 0, items = 0;
    const walk = (ns) => (ns || []).forEach((n) => { nodes++; if (n.level === 0) topics++; items += n.item_count || 0; walk(n.children); });
    walk(tree);
    return { topics, nodes, items };
  }, [tree]);

  // ---- edits ----
  const doAdd = async (parentId) => {
    const name = addName.trim(); if (!name) return;
    setBusy(true);
    try {
      await APIService.createHubNode({ parentId: parentId === 'ROOT' ? null : parentId, name });
      setAddName(''); setAddUnder(null);
      if (parentId !== 'ROOT') setExpanded((p) => new Set(p).add(parentId));
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const doRename = async (id) => {
    const name = renameVal.trim(); if (!name) { setRenaming(null); return; }
    setBusy(true);
    try { await APIService.updateHubNode(id, { name }); setRenaming(null); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const doDelete = async (node) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${node.name}" and everything under it?`)) return;
    setBusy(true);
    try { await APIService.deleteHubNode(node.id); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // ---- table rows (returns a flat array) ----
  const renderRow = (node, depth) => {
    const hasKids = node.children && node.children.length > 0;
    const expandable = hasKids || node.item_count > 0 || node.kind === 'node';
    const open = expanded.has(node.id);
    const st = ks[node.id];
    const m = st && st.attempts > 0 ? st.mastery : null;
    const pad = 14 + depth * 18;
    const out = [
      <div key={node.id} className="kh-row" style={{ paddingLeft: pad }}>
        {expandable
          ? <span className="kh-caret" onClick={() => toggleNode(node)}><Caret open={open} /></span>
          : <span className="kh-caret" />}
        <span className={`kh-name ${node.level === 0 ? 'topic' : ''}`}>{node.name}</span>
        <span className="kh-kind">{node.kind}</span>
        {node.item_count ? <span className="kh-count">· {node.item_count} items</span> : null}
        <span className="kh-spacer" />
        {m != null && (
          <>
            <span className="kh-mbar"><i className={masteryClass(m)} style={{ width: `${Math.round(m * 100)}%` }} /></span>
            <span className="kh-mpct">{Math.round(m * 100)}%</span>
          </>
        )}
        <span className="kh-rowedit">
          <button className="kh-iconbtn" title="Add child" onClick={() => { setAddUnder(node.id); setAddName(''); }}><Plus /></button>
          <button className="kh-iconbtn" title="Rename" onClick={() => { setRenaming(node.id); setRenameVal(node.name); }}><Pencil /></button>
          <button className="kh-iconbtn danger" title="Delete" onClick={() => doDelete(node)}><Trash /></button>
        </span>
      </div>,
    ];
    if (renaming === node.id) out.push(
      <div key={`${node.id}-ren`} className="kh-inlineform" style={{ paddingLeft: pad + 26 }}>
        <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && doRename(node.id)} />
        <button className="kh-btn primary" disabled={busy} onClick={() => doRename(node.id)}>Save</button>
        <button className="kh-btn" onClick={() => setRenaming(null)}>Cancel</button>
      </div>);
    if (addUnder === node.id) out.push(
      <div key={`${node.id}-add`} className="kh-inlineform" style={{ paddingLeft: pad + 44 }}>
        <input autoFocus placeholder="New sub-topic / concept name…" value={addName}
          onChange={(e) => setAddName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doAdd(node.id)} />
        <button className="kh-btn primary" disabled={busy} onClick={() => doAdd(node.id)}>Add</button>
        <button className="kh-btn" onClick={() => setAddUnder(null)}>Cancel</button>
      </div>);
    if (hasKids && open) node.children.forEach((c) => out.push(...renderRow(c, depth + 1)));

    // Leaf content: the node's questions (hub_items), shown as tabular sub-rows.
    if (open && (node.item_count > 0 || node.kind === 'node')) {
      const its = items[node.id];
      const ipad = pad + 34;
      if (its === undefined) {
        out.push(<div key={`${node.id}-il`} className="kh-row" style={{ paddingLeft: ipad, color: 'var(--n-text-3)' }}>Loading questions…</div>);
      } else {
        its.forEach((it) => out.push(
          <div key={it.id} className="kh-row kh-itemrow" style={{ paddingLeft: ipad }}>
            <span className="kh-itemq">{(it.payload && (it.payload.question || it.payload.title)) || it.type}</span>
            <span className="kh-spacer" />
            <span className="kh-rowedit">
              <button className="kh-iconbtn danger" title="Delete question" onClick={() => doDeleteItem(node.id, it.id)}><Trash /></button>
            </span>
          </div>));
        if (addingItem === node.id) out.push(
          <div key={`${node.id}-additem`} className="kh-inlineform" style={{ paddingLeft: ipad }}>
            <input autoFocus placeholder="New question…" value={itemText}
              onChange={(e) => setItemText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doAddItem(node.id)} />
            <button className="kh-btn primary" disabled={busy} onClick={() => doAddItem(node.id)}>Add</button>
            <button className="kh-btn" onClick={() => setAddingItem(null)}>Cancel</button>
          </div>);
        else out.push(
          <div key={`${node.id}-addq`} className="kh-row" style={{ paddingLeft: ipad }}>
            <button className="kh-iconbtn" title="Add question" onClick={() => { setAddingItem(node.id); setItemText(''); }}><Plus /></button>
            <span style={{ fontSize: 12, color: 'var(--n-text-3)' }}>Add question</span>
          </div>);
      }
    }
    return out;
  };

  // ---- graph (radial) ----
  const kids = childrenOf[focus] || [];
  const crumbs = useMemo(() => {
    const p = []; let cur = focus;
    while (cur && cur !== 'ROOT') { const n = byId[cur]; if (!n) break; p.unshift(n); cur = parentOf(cur); }
    return p;
  }, [focus, byId]);
  const R = 38;
  const placed = kids.map((k, i) => {
    const ang = (-90 + i * (360 / Math.max(1, kids.length))) * (Math.PI / 180);
    return { ...k, x: 50 + R * Math.cos(ang), y: 50 + R * Math.sin(ang) };
  });

  return (
    <div className="kh">
      <div className="kh-head">
        <div>
          <h1>Knowledge Hub</h1>
          <p>Everything the app can teach — one taxonomy every mode draws from, coloured by what you've mastered. Edit it freely.</p>
        </div>
        <div className="kh-actions">
          <div className="kh-toggle">
            <button className={view === 'table' ? 'on' : ''} onClick={() => setView('table')}>Table</button>
            <button className={view === 'graph' ? 'on' : ''} onClick={() => setView('graph')}>Graph</button>
          </div>
          <label className={`kh-btn kh-upload${importing ? ' busy' : ''}`} title="Create a new topic from a Markdown file — headings become structure, bullets and question lines become questions">
            {importing ? 'Importing…' : <><IconUpload size={14} /> Upload Markdown</>}
            <input
              type="file"
              accept=".md,.markdown,text/markdown"
              style={{ display: 'none' }}
              disabled={importing}
              onChange={importMarkdownFile}
            />
          </label>
          <button className="kh-btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {err && <div className="kh-banner err">{err}</div>}

      <div className="kh-stats">
        <div className="kh-stat"><b>{counts.topics}</b><span>topics</span></div>
        <div className="kh-stat"><b>{counts.nodes}</b><span>nodes</span></div>
        <div className="kh-stat"><b>{counts.items}</b><span>items</span></div>
      </div>

      {loading && tree.length === 0 ? (
        <div className="kh-empty">Loading your Knowledge Hub…</div>
      ) : tree.length === 0 ? (
        <div className="kh-empty">The Knowledge Hub is empty. Seed it from the standardized syllabus, or add a topic below.</div>
      ) : view === 'table' ? (
        <>
          <div className="kh-tree">{tree.flatMap((n) => renderRow(n, 0))}</div>
          {addUnder === 'ROOT' ? (
            <div className="kh-inlineform" style={{ marginTop: 10, borderRadius: 10, border: '1px solid var(--n-border)' }}>
              <input autoFocus placeholder="New top-level topic…" value={addName}
                onChange={(e) => setAddName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doAdd('ROOT')} />
              <button className="kh-btn primary" disabled={busy} onClick={() => doAdd('ROOT')}>Add topic</button>
              <button className="kh-btn" onClick={() => setAddUnder(null)}>Cancel</button>
            </div>
          ) : (
            <button className="kh-btn" style={{ marginTop: 10 }} onClick={() => { setAddUnder('ROOT'); setAddName(''); }}>+ Add a top-level topic</button>
          )}
        </>
      ) : (
        <div className="kh-graphwrap">
          <div className="kh-crumbs">
            <button className={`kh-crumb${focus === 'ROOT' ? ' here' : ''}`} onClick={() => setFocus('ROOT')}>All</button>
            {crumbs.map((n) => (
              <React.Fragment key={n.id}>
                <span style={{ color: 'var(--n-text-3)' }}>›</span>
                <button className={`kh-crumb${focus === n.id ? ' here' : ''}`} onClick={() => setFocus(n.id)}>{n.name}</button>
              </React.Fragment>
            ))}
          </div>
          {kids.length === 0 ? (
            <div className="kh-empty">This node has no children. Use the Table view to add or edit content here.</div>
          ) : (
            <div className="kh-radial">
              <svg className="kh-edges" viewBox="0 0 100 100" preserveAspectRatio="none">
                {placed.map((k) => <line key={k.id} x1="50" y1="50" x2={k.x} y2={k.y} stroke="var(--n-border-2)" strokeWidth="0.4" />)}
              </svg>
              <div className="kh-center">
                <div className="t">{focus === 'ROOT' ? 'Knowledge Hub' : byId[focus]?.name}</div>
                <div className="s">{kids.length} {focus === 'ROOT' ? 'topics' : 'children'}</div>
              </div>
              {placed.map((k) => {
                const st = ks[k.id]; const m = st && st.attempts > 0 ? st.mastery : null;
                const drillable = (childrenOf[k.id] || []).length > 0;
                return (
                  <div key={k.id} className="kh-gnode" style={{ left: `${k.x}%`, top: `${k.y}%` }}
                    onClick={() => drillable && setFocus(k.id)} title={drillable ? 'Drill in' : k.name}>
                    <div className="disc" style={{ background: masteryColor(m), color: m != null ? '#0b0d14' : 'var(--n-text)' }}>
                      {m != null ? `${Math.round(m * 100)}%` : (k.item_count || (k.meta && k.meta.count) || '')}
                    </div>
                    <div className="lab">{k.name}</div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="kh-legend">
            <span><i style={{ background: '#10b981' }} /> strong</span>
            <span><i style={{ background: '#f59e0b' }} /> mixed</span>
            <span><i style={{ background: '#ef4444' }} /> weak</span>
            <span><i style={{ background: 'var(--n-border-2)' }} /> untouched</span>
          </div>
        </div>
      )}
    </div>
  );
}
